import type { ErrorResponse } from '@1webit/cvs-api-scheme';
import { getApi } from '@1webit/cvs-api-scheme';

import { Client } from '@/client';

export type ApiMethod = keyof ReturnType<typeof getApi>;

type Api = ReturnType<typeof getApi>;

export type ApiMethodArgs = {
  [K in keyof Api]: Parameters<Api[K]>;
};

export type ApiMethodReturnTypes = {
  [K in keyof Api]: ReturnType<Api[K]>;
};

interface SingleOfficeErrorResponse {
  status: 'error';
  code: number;
  message: string;
  context: {
    validation: {
      [key: string]: string;
    };
  };
}

type ArrayOfficeErrorResponse = Array<{
  code: number;
  message: string;
}>;

type OfficeErrorResponse = ArrayOfficeErrorResponse | SingleOfficeErrorResponse;

export type ServerResponse<T> = T | OfficeErrorResponse | ErrorResponse;

export interface FormActionError {
  status: string;
  code: number;
  message: string;
  context: {
    validation?: Record<string, string[]>;
  };
}

export interface ServerActionError {
  message: string;
}

function isErrorResponse(data: unknown): data is ErrorResponse {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }

  return (
    'status' in data
    && data.status === 'error'
    && 'code' in data
    && typeof data.code === 'number'
    && (data.code.toString().startsWith('4')
      || data.code.toString().startsWith('5'))
    && 'message' in data
    && typeof data.message === 'string'
  );
}

function isSingleOfficeErrorResponse(
  data: unknown,
): data is SingleOfficeErrorResponse {
  return (
    typeof data === 'object'
    && data !== null
    && 'status' in data
    && data.status === 'error'
    && 'code' in data
    && typeof data.code === 'number'
    && (data.code.toString().startsWith('4')
      || data.code.toString().startsWith('5'))
    && 'message' in data
    && typeof data.message === 'string'
    && 'context' in data
    && typeof data.context === 'object'
    && data.context !== null
    && 'validation' in data.context
    && typeof data.context.validation === 'object'
  );
}

function isServerActionError(data: unknown): data is ServerActionError {
  return (
    typeof data === 'object'
    && data !== null
    && 'message' in data
    && typeof data.message === 'string'
    && !('code' in data && data.code === 200)
  );
}

function isArrayOfficeErrorResponse(
  data: unknown,
): data is ArrayOfficeErrorResponse {
  return (
    typeof data === 'object'
    && data !== null
    && Array.isArray(data)
    && data.length > 0
    && data.every(
      item =>
        'code' in item
        && typeof item.code === 'number'
        && (item.code.toString().startsWith('4')
          || item.code.toString().startsWith('5'))
        && 'message' in item
        && typeof item.message === 'string',
    )
  );
}

const errorToString = (error: unknown, fallback?: string) => {
  if (typeof error === 'string') {
    return error;
  }
  if (isArrayOfficeErrorResponse(error)) {
    return error.map(item => item.message).join(';');
  }
  if (isSingleOfficeErrorResponse(error)) {
    return Object.entries(error.context.validation).join(';');
  }

  if (
    error instanceof Error
    || isErrorResponse(error)
    || isServerActionError(error)
  ) {
    return error.message;
  }

  return fallback ?? 'Něco se pokazilo';
};

export const isResponseValid = <T>(response: ServerResponse<T> | ServerActionError): response is ServerResponse<T> => {
  return !(isErrorResponse(response)
    || isSingleOfficeErrorResponse(response)
    || isArrayOfficeErrorResponse(response)
    || isServerActionError(response));
};

export const validateResponse = <T>(
  response: ServerResponse<T> | ServerActionError,
): T => {
  if (!isResponseValid(response)) {
    throw new Error(errorToString(response));
  }

  return response as T;
};
export const ignoredErrors = (endpoint: string) => {
  return [
    'getApiV1PhotoUserImageId',
    'getApiV1PhotoUserId',
    'postApiV1User',
    'getApiV1RequestFormBeachTournamentApplicationCheck',
  ].includes(endpoint);
};

export const getResponse = async <T extends ApiMethod>(
  method: T,
  ...args: ApiMethodArgs[T]
): Promise<Exclude<Awaited<ApiMethodReturnTypes[T]>, ErrorResponse>> => {
  const client = Client.getInstance();
  const api = getApi(client);

  if (typeof api[method] !== 'function') {
    throw new TypeError(`Method ${String(method)} not found in API`);
  }

  const response = await (api[method] as any)(...args);

  if (ignoredErrors(method)) {
    return response;
  }

  const isValidResponse = isResponseValid(response);

  if (!isValidResponse) {
    return Promise.reject(response);
  }

  return response;
};

export {
  errorToString,
  isArrayOfficeErrorResponse,
  isErrorResponse,
  isServerActionError,
  isSingleOfficeErrorResponse,
};
